/**
 * Job Health Grid Component
 * Displays health status for all job/account combinations in a card grid
 */

import { CheckCircle, XCircle } from 'lucide-react'
import { cn, formatDate, formatDuration } from '@/lib/utils'
import type { BatchJobHealthResponse } from '../types'

const JOB_DISPLAY_NAMES: Record<string, string> = {
  CustomerMetricsSync: 'Customer Metrics Sync',
  AccountBaselineSync: 'Account Baseline Sync',
  DailyEventMetrics: 'Daily Event Metrics',
  DomainExclusionListCount: 'Domain Exclusion List Count',
}

interface JobHealthGridProps {
  health: BatchJobHealthResponse
}

export function JobHealthGrid({ health }: JobHealthGridProps) {
  const { byJob } = health

  if (!byJob || Object.keys(byJob).length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
        No health data available.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4">
      {Object.entries(byJob).map(([jobName, accountsMap]) => {
        const displayName = JOB_DISPLAY_NAMES[jobName] ?? jobName
        const entries = Object.entries(accountsMap)
        const anyFailing = entries.some(([, h]) => h.status === 'FAILING')

        return (
          <div
            key={jobName}
            className={cn(
              'bg-white rounded-xl border p-4 shadow-sm',
              anyFailing ? 'border-red-300' : 'border-green-300',
            )}
          >
            {/* Card header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">{displayName}</h3>
              {anyFailing ? (
                <span className="flex items-center gap-1 text-xs font-medium text-red-600">
                  <XCircle className="h-4 w-4" />
                  Failing
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Healthy
                </span>
              )}
            </div>

            {/* Account rows */}
            <div className="space-y-2">
              {entries.map(([accountKey, accountHealth]) => {
                const label =
                  accountKey === 'null' || accountKey === 'ALL'
                    ? 'All Accounts'
                    : `Account ${accountKey}`
                const isHealthy = accountHealth.status === 'HEALTHY'

                return (
                  <div
                    key={accountKey}
                    className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm"
                  >
                    <div className="flex items-center gap-2">
                      {isHealthy ? (
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      )}
                      <span className="font-medium text-gray-700">{label}</span>
                    </div>
                    <div className="text-right text-xs text-gray-500 space-y-0.5">
                      {accountHealth.lastRunAt && (
                        <div>{formatDate(accountHealth.lastRunAt)}</div>
                      )}
                      {accountHealth.lastDurationMs != null && (
                        <div className="text-gray-400">{formatDuration(accountHealth.lastDurationMs)}</div>
                      )}
                      {!isHealthy && accountHealth.errorMessage && (
                        <div className="text-red-500 max-w-[200px] truncate" title={accountHealth.errorMessage}>
                          {accountHealth.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
